import { useEffect, useState, useCallback, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { formatBytes } from "../utils/ByteConverter";
import { debounce } from "@mui/material/utils";
import { Add } from "@mui/icons-material";

export const UnclaimedResources = ({ areaId }) => {
  const { authState } = useOktaAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "display_name", sort: "asc" }]);
  const [filterModel, setFilterModel] = useState({ items: [], logicOperator: "and" });

  // --- Fetch data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { page, pageSize } = paginationModel;
      const sort = sortModel[0] ?? {};

      const params = new URLSearchParams({
        page: page + 1,
        pageSize,
        sortColumn: sort.field,
        sortDirection: sort.sort,
      });

      if (filterModel.items.length > 0) {
        params.set("filters", JSON.stringify(filterModel));
      }

      const url = `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}resources/unclaimed?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          Authorization: authState.accessToken.accessToken,
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error(`Failed to fetch data: ${res.statusText}`);

      const data = await res.json();
      console.log(data.resources);
      setRows(data.resources);
      setRowCount(data.totalRows);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unknown error occurred");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModel, authState.accessToken?.accessToken]);

  const fetchDataDebounced = useCallback(
      debounce(() => fetchData(), 600),
      [fetchData]
  );

  useEffect(() => {
    fetchDataDebounced();
    return fetchDataDebounced.clear;
  }, [fetchDataDebounced]);

  const claimResource = async () => {
    if (!selectedResourceId) return;
    setProcessing(true);
    setProcessingError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}resources/unclaimed`, {
        method: "POST",
        headers: {
          Authorization: authState.accessToken.accessToken,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: selectedResourceId,
          areaId,
        }),
      });

      if (!res.ok) throw new Error(`Failed to claim resource: ${res.statusText}`);

      await res.json();
      setDialogVisible(false);
      fetchData(); // Refresh table
    } catch (err) {
      console.error(err);
      setProcessingError(err.message || "Unknown error");
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 150 },
    { field: "display_name", headerName: "Display Name", width: 200, filterable: true },
    { field: "service", headerName: "Resource Type", width: 200, filterable: true},
    { field: "owner_display_name", headerName: "Owner", width: 200, filterable: true },
    {
      field: "storage_used_byte",
      headerName: "Current Usage",
      width: 150,
      valueFormatter: (value ) => formatBytes(value, 2),
      filterable: false,
    },
    { field: "report_refresh_date", headerName: "Last Refresh", width: 200, filterable: false },
    { field: "primary_role_employee", headerName: "Employee Type", width: 150, filterable: true },
    {
      field: "add_resource",
      headerName: "Add Resource To Area",
      width: 50,
      sortable: false,
      filterable: false,
      flex: 1,
      cellClassName: "centeredCell",
      renderCell: (params) => (
                        <Tooltip 
                          title="Add this resource to your area"
                          placement="bottom"
                          >
        <IconButton
          onClick={() => {
            setSelectedResourceId(params.id);
            setProcessingError(null);
            setDialogVisible(true);
          }}
        >
          <Add />
        </IconButton></Tooltip>
      ),
    }
  ];

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <DataGrid
        sx={{
          "& .MuiCircularProgress-root": { color: "#A60F2D" },
          "& .centeredCell": { display: "flex", alignItems: "center" },
        }}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          if (JSON.stringify(model) === JSON.stringify(filterModel)) return;
          setFilterModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        pageSizeOptions={[10, 15, 25]}
        components={{
          NoRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: "center" }}>
              {error ? `Error: ${error}` : "No rows"}
            </Box>
          ),
        }}
      />

      <Dialog open={dialogVisible} onClose={() => setDialogVisible(false)}>
        <DialogContent>
          {processing ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}
            >
              <CircularProgress size={50} sx={{ color: "#A60F2D" }} />
            </Box>
          ) : processingError ? (
            <Typography color="error">{processingError}</Typography>
          ) : (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell style={{border: "none"}}>{`Are you sure you want to associate ${selectedResourceId} with area ${areaId}?`}</TableCell>
                  <TableCell style={{border: "none"}}>
                    <Button onClick={claimResource} variant="contained" style={{backgroundColor: "#A60F2D", color: "white"}} >
                      Yes
                    </Button>
                  </TableCell>
                  <TableCell style={{border: "none"}}>
                    <Button onClick={() => setDialogVisible(false)} variant="contained" style={{backgroundColor: "#A60F2D", color: "white"}} >
                      No
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
